import type { RouteLocationAsRelative, Router } from 'vue-router'

import { ROUTE_COMMUNITY } from '~/contexts/community/router/routes'
import { ROUTE_DM } from '~/contexts/dm/router/routes'
import { ROUTE_HOME } from '~/contexts/home/router/routes'
import { ROUTE_ME } from '~/contexts/me/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'
import { ROUTE_RITUAL } from '~/contexts/ritual/router/routes'

import { router } from '~/router/router'

import type { CommunityRoute, CommunityType } from '~/entities/container/community/interfaces/community'
import { useCommunitiesStore } from '~/entities/container/community/stores/communities'
import { useConversationsStore } from '~/entities/container/conversation/stores/conversations'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { useCommunityFeedMode } from '~/features-shared/community/stores/states'
import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { fetchSwymContent } from '~/features-shared/content/api/contents'
import { getContentTypeNotProcessedFromModel } from '~/features-shared/content/composables/content-types'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { EFeedMode } from '~/features-shared/content/stores/contents'
import type { IDerivedTypeData } from '~/features-shared/content-derived-type/interfaces/derived-types'
import { displayContext } from '~/features-shared/left-panel/composables/left-panel-manager'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

/**
 * Go to the community or conversation and open the content in detail view
 * @param {string} threadId      - community or conversation of the destination thread
 * @param {string} communityType - context to navigate
 * @param {IContent} content     - content to display in detail view or in the lightbox for a media content
 */
export async function goToThreadAndOpenContent(
  threadId: SUBJECT_6W_URI,
  communityType: 'dm' | 'community',
  content: Pick<IContent, 'subject_uri'> & { derived_type?: Partial<IDerivedTypeData> },
) {
  const contentType = content.subject_uri.includes(IContentModel.Media) ? 'media' : 'content'

  await router.push({
    name: communityType === 'dm' ? ROUTE_DM.URI_HOME : ROUTE_COMMUNITY.URI_HOME,
    params: { thread_id: threadId },
    query: { [contentType]: content.subject_uri, derivedTypeId: content.derived_type?.id },
  })

  // update the left column
  if (communityType === 'dm')
    displayContext(EContext.DM)
  else
    displayContext(EContext.COMMUNITY)
}

/**
 * Get detail view URL from a content
 * @param content Content used to build the URL
 * @param edit Should the URL open the edit mode?
 * @param clearHash Should the routing clear the hash?
 * @param section Section added to url for TOC anchors
 */
export function getDetailViewUrl(
  content: Pick<IContent, 'subject_uri'> & { derived_type?: Partial<IDerivedTypeData> },
  edit?: boolean,
  clearHash?: boolean,
  community_type?: string,
  section?: string,
): ReturnType<Router['resolve']> {
  if (content.subject_uri.includes(IContentModel.Media)) {
    // Don't add query in dashboard widget user profile (IR-1261442)
    const contentQuery = ('profile' in router.currentRoute.value.query) ? {} : { ...router.currentRoute.value.query }

    return router.resolve({
      ...router.currentRoute.value,
      ...(clearHash ? { hash: '' } : undefined),
      query: {
        ...contentQuery,
        media: content.subject_uri,
        edit: edit ? edit.toString() : undefined,
      },
    })
  }
  else if (content.subject_uri.includes(IContentModel.Rituals)) {
    return router.resolve({
      name: ROUTE_RITUAL.URI_HOME,
      params: { thread_id: content.subject_uri },
    })
  }
  else if (community_type === 'personalspace') {
    return router.resolve({
      name: ROUTE_ME.PERSONAL_SPACE_HOME,
      query: { content: content.subject_uri, section },
    })
  }
  else {
    return router.resolve({
      ...router.currentRoute.value,
      ...(clearHash ? { hash: '' } : undefined),
      query: {
        content: content.subject_uri,
        edit: edit ? edit.toString() : undefined,
        derivedTypeId: content.derived_type?.id,
        section,
      },
    })
  }
}

/**
 * Returns the URL of the a thread
 *
 * @param threadUri The URI of the thread
 * @param threadType The type. Can be 'dm', 'community' or 'personalspace'
 * @returns The URL of the thread
 */
export function getThreadUrl(
  threadType: CommunityType,
  threadUri?: SUBJECT_6W_URI,
) {
  return router.resolve({
    name: threadType === 'personalspace'
      ? ROUTE_ME.PERSONAL_SPACE_HOME
      : (threadType === 'community' ? ROUTE_COMMUNITY.URI_HOME : ROUTE_DM.URI_HOME),
    params: { thread_id: threadUri! },
  })
}

export function getThreadAndDetailViewUrl(
  content: Pick<IContent, 'subject_uri'> & { derived_type?: Partial<IDerivedTypeData> },
  threadUri: SUBJECT_6W_URI,
  threadType: CommunityType,
) {
  const resolvedDetailView = getDetailViewUrl(content)
  return router.resolve({
    ...resolvedDetailView,
    name: threadType === 'personalspace'
      ? ROUTE_ME.PERSONAL_SPACE_HOME
      : (threadType === 'community' ? ROUTE_COMMUNITY.URI_HOME : ROUTE_DM.URI_HOME),
    params: { thread_id: threadUri },
  })
}

/**
 * Open the content in detail view
 * @param {IContent} content - content to display in detail view or in the lightbox for a media content
 */
export function goToDetailView(
  content: Pick<IContent, 'subject_uri'> & { derived_type?: Partial<IDerivedTypeData> },
  edit?: boolean,
  clearHash?: boolean,
  community_type?: string,
) {
  return router.push(getDetailViewUrl(content, edit, clearHash, community_type))
}

/**
 * Close the detail view, keep query not related to content
 *
 * Removes:
 * - query content
 * - query derivedTypeId
 * - query edit
 */
export async function closeDetailView() {
  await router.push({
    query: {
      ...router.currentRoute.value.query,
      content: undefined,
      derivedTypeId: undefined,
      edit: undefined,
    },
  })
}

/**
 * Close the lightbox view, keep query not related to lightbox
 *
 * Removes:
 * - query media
 * - query derivedTypeId
 */
export async function closeMediaLightbox() {
  await router.push({
    query: {
      ...router.currentRoute.value.query,
      media: undefined,
      edit: undefined,
      panel: undefined,
      annotation: undefined,
    },
  })
}

/**
 * Leave edit mode of the content detail view, keep query not related to lightbox
 *
 * Removes:
 * - query edit
 */
export async function closeDetailViewEdit() {
  await router.push({ query: { ...router.currentRoute.value.query, edit: undefined } })
}

/**
 * Resolve the community whatsnew page with the given community URI
 * @param communityUri Community URI to use for navigation
 */
export function getCommunityUrl(communityUri: SUBJECT_6W_URI) {
  return router.resolve({ name: ROUTE_COMMUNITY.URI_HOME, params: { thread_id: communityUri } })
}

/**
 * Go to the community whatsnew page. Change the community feed mode if provided
 * and force the context to be on community
 * @param communityUri Community URI to use for navigation
 * @param feedMode : optional params with large mode by default
 */
export async function goToCommunity(communityUri: SUBJECT_6W_URI, feedMode = EFeedMode.Large) {
  if (feedMode) {
    const feed_mode = useCommunityFeedMode()
    feed_mode.value = feedMode
  }
  await router.push(getCommunityUrl(communityUri))
  // update the left column
  displayContext(EContext.COMMUNITY)
}

/**
 * Go to the conversation main page
 * and force the context to be on conversation
 * @param conversationUri conversation URI to use for navigation
 */
export async function goToConversation(conversationUri?: SUBJECT_6W_URI) {
  if (conversationUri)
    await router.push({ name: ROUTE_DM.URI_HOME, params: { thread_id: conversationUri } })
  else
    await router.push({ name: ROUTE_DM.HOME })
  // update the left column
  displayContext(EContext.DM)
}

/**
 * Go to a container that has tags enabled (community, V1 conversations)
 * @param threadUri URI to use for navigation
 * @returns true if navigated, false otherwise
 */
export async function goToTaggableContainer(threadUri: SUBJECT_6W_URI) {
  const community = (await useCommunitiesStore()
    .getOrFetchCommunity(threadUri))

  // If it is a community, navigate to it
  if (community) {
    await goToCommunity(threadUri)
    return true
  }

  // Check if it is an rtc
  const isRTC = useConversationsStore()
    .isConversationFromV2(threadUri)

  if (isRTC)
    return false // Do not navigate in case of RTC

  // Navigate to the conversation otherwise
  await goToConversation(threadUri)
  return true
}

/**
 * Return user profile location path regarding its login/hashkey.
 * - navigate to the profile lightbox
 *
 * Note: Hashkey is used in priority
 * @param {string} login
 * @param {string} hashkey
 * @returns {RouteLocationAsRelative}
 */
export function getUserProfilePath(login: string, hashkey?: string): RouteLocationAsRelative {
  return {
    query: {
      ...router.currentRoute.value.query,
      profile: hashkey || login,
      tab: undefined,
    },
  }
}

/**
 * Return user profile location path regarding its login/hashkey.
 * - navigate to the profile lightbox
 *
 * Note: Hashkey is used in priority
 * @param {string} login
 * @param {string} hashkey
 * @returns {RouteLocation}
 */
export function getUserProfileUrl(login: string, hashkey?: string) {
  return router.resolve(getUserProfilePath(login, hashkey))
}

/**
 * Resolves ME URL of given page
 * @param name - name of ME page
 * @returns {RouteLocation}
 */
export function getMePage(name: ROUTE_ME) {
  return router.resolve({ name })
}

/**
 * Go to the given page. This navigation will clear queries
 * @param name Page name
 */
export const goToPageName = async (name: RouteLocationAsRelative['name']) => router.push({ name } as RouteLocationAsRelative)

/**
 * Resolve profile URL
 *
 * Will fetch the current user to choose between profile-lightbox or me-profile page
 *
 * @param profile hashkey or login
 */
export async function getProfileUrl(profile: string) {
  await useUsersStore()
    .fetchCurrentUser()
  const currentUser = useUsersStore().currentUser

  // profile is connected user
  if ((currentUser?.hash_key === profile) || (currentUser?.login === profile))
    return router.resolve({ name: ROUTE_ME.PROFILE_HOME })

  return router.resolve({ name: ROUTE_HOME.HOME, query: { profile } })
}

/**
 * Resolve profile network URL
 *
 * Will fetch the current user to choose between network or network-user-network page
 *
 * @param profile hashkey or login
 */
export async function getProfileNetworkUrl(profile: string) {
  await useUsersStore()
    .fetchCurrentUser()
  const currentUser = useUsersStore().currentUser
  // profile is connected user
  if ((currentUser?.hash_key === profile) || (currentUser?.login === profile))
    return router.resolve({ name: ROUTE_NETWORK.ACTIVITY })

  return router.resolve({ name: ROUTE_NETWORK.USER_NETWORK, params: { thread_id: profile } })
}
export function getDMUrl(communityUri: SUBJECT_6W_URI) {
  return router.resolve({ name: ROUTE_DM.URI_HOME, params: { thread_id: communityUri } })
}

/**
 * Resolve profile activity URL
 *
 * Will fetch the current user to choose between me-contribution or network-user-activity page
 *
 * @param profile hashkey or login
 */
export async function getProfileActivityUrl(profile: string) {
  await useUsersStore()
    .fetchCurrentUser()
  const currentUser = useUsersStore().currentUser

  // profile is connected user
  if ((currentUser?.hash_key === profile) || (currentUser?.login === profile))
    return router.resolve({ name: ROUTE_ME.CONTRIBUTIONS_HOME })

  return router.resolve({ name: ROUTE_NETWORK.USER_ACTIVITY, params: { thread_id: profile } })
}

/**
 * Get community raw route from extracted data from v2 URL
 *
 * - Default to community whatsnew
 * - If content type, navigate to the dedicated page for this type
 * - If content id, navigate to the detail view + community whatsnew in background
 *
 * @param communityData data extracted from v2-url
 * @param communityData.communityType Type of community
 * @param communityData.contentId content URI
 * @param communityData.derivedTypeId derived type ID if the content is a derived type
 * @param communityData.contentType content type, post, media, idea, etc..
 */
export async function getCommunityOrConversationRoute(communityData: CommunityRoute): Promise<RouteLocationAsRelative> {
  // Get the thread ID to construct v3 route of the container. It can be a community or a DM
  const thread_id: SUBJECT_6W_URI | undefined = communityData.communityType === 'community'
    ? (await useCommunitiesStore()
      .getOrFetchCommunity(communityData.communitySubjectUri as string))?.subject_uri
    : (await useConversationsStore()
      .fetchConversation(communityData.communitySubjectUri as string))?.subject_uri

  const path: RouteLocationAsRelative = {
    name: communityData.communityType === 'community' ? ROUTE_COMMUNITY.URI_HOME : ROUTE_DM.URI_HOME,
    params: { thread_id: thread_id! },
  }

  // If there is some content data in the community route data, then construct content route as well
  if (communityData?.contentId) {
    const content = (await fetchSwymContent(
      communityData.contentId,
      false,
      communityData.contentType,
    ))
    if (content) {
      // To get detail view route of the content
      const resolvedRoute = getDetailViewUrl(content)
      return {
        ...path,
        query: resolvedRoute.query,
      }
    }
  }

  // If there is contentType in the route data, then the feed must be filtered on that content type
  if (communityData?.contentType) {
    if (communityData.contentType === IContentModel.DerivedIdea) {
      return {
        name: ROUTE_COMMUNITY.URI_FACETS_DT_IDEA,
        params: { thread_id: thread_id!, derived_type_id: communityData.derivedTypeId! },
      }
    }
    if (communityData.contentType === IContentModel.DerivedPost) {
      return {
        name: ROUTE_COMMUNITY.URI_FACETS_DT_POST,
        params: { thread_id: thread_id!, derived_type_id: communityData.derivedTypeId! },
      }
    }

    const contentType = getContentTypeNotProcessedFromModel(communityData.contentType)
    return {
      name: contentType?.routes[
        communityData.communityType === 'community'
          ? EContext.COMMUNITY
          : EContext.DM
      ]?.name,
      params: { thread_id, content_type: communityData.contentType },
    } as RouteLocationAsRelative
  }
  return path
}

/**
 * Resolve community route from extracted data from v2 URL
 *
 * - Default to community whatsnew
 * - If content type, navigate to the dedicated page for this type
 * - If content id, navigate to the detail view + community whatsnew in background
 *
 * @param communityData Community data object
 * @param communityData.communityType Type of community
 * @param communityData.contentId content URI
 * @param communityData.derivedTypeId derived type ID if the content is a derived type
 * @param communityData.contentType content type, post, media, idea, etc..
 */
export async function getCommunityOrContentUrl(communityData: CommunityRoute) {
  return router.resolve(await getCommunityOrConversationRoute(communityData))
}

/**
 * Resolve comment or reply route from extracted data from v2 URL
 *
 * - Default to community whatsnew
 * - If content type, navigate to the dedicated page for this type
 * - If content id, navigate to the detail view + community whatsnew in background
 *
 * @param data Community data object
 * @param data.communitySubjectUri community URI
 * @param data.contentId content URI
 * @param data.comment comment URI
 * @param data.reply reply URI
 * @param data.communityType community or dm
 */
export async function getCommentOrReplyUrl(data: CommunityRoute) {
  const path = await getCommunityOrConversationRoute(data)

  return router.resolve({
    ...path,
    query: {
      ...path.query,
      comment: data.comment,
      reply: data.reply,
      answer: data.answer,
    },
    replace: true,
  })
}

/**
 * Resolve community URL to a specific page
 * @param communityData Community data
 * @param communityData.communitySubjectUri Community URI
 * @param communityData.page 'members' | 'settings' | 'statistics'
 * @param communityData.communityType 'community' | 'dm
 */
export async function getCommunityPageUrl(communityData: CommunityRoute) {
  return router.resolve({
    ...await getCommunityOrConversationRoute(communityData),
    name: `${communityData.communityType}-thread_id-${communityData.page}` as ROUTE_COMMUNITY,
  })
}

/**
 * Resolve route to a specific message inside a conversation
 * @param communityData Community data
 * @param communityData.messageId Message ID
 */
export async function getDmMessageUrl(communityData: CommunityRoute) {
  const path = await getCommunityOrConversationRoute(communityData)
  return router.resolve({
    ...path,
    query: {
      ...path.query,
      messageId: communityData.messageId,
    },
    replace: true,
  })
}

/**
 * Go to profile page of the given user login.
 * Also redirect the user to home page if app is still on root page (/)
 *
 * - If login = current user, go to me-profile
 * - Else, open profile lightbox
 *
 * @param login user login
 */
export async function goToProfile(login: string, replace = false) {
  const userStore = useUsersStore()
  if (!userStore.currentUser)
    await userStore.fetchCurrentUser()

  // Check
  const name = router.currentRoute.value.name === ROUTE_HOME.LANDING
    ? ROUTE_HOME.HOME
    : (router.currentRoute.value.name || ROUTE_HOME.HOME)

  // V2 url can have login as well as hash key, hence pass the same parameter.
  const userProfileRoute = getUserProfileUrl(login, login)
  // To handle other user profile
  return replace
    ? router.replace({
      ...userProfileRoute,
      name: userProfileRoute.name === ROUTE_ME.PROFILE_HOME ? ROUTE_ME.PROFILE_HOME : name,
    })
    : router.push({
      ...userProfileRoute,
      name: userProfileRoute.name === ROUTE_ME.PROFILE_HOME ? ROUTE_ME.PROFILE_HOME : name,
    })
}

/**
 * Resolve ritual(s) URL
 * @param thread_id Ritual URI, optional, if given, it will resolve the route to this ritual
 */
export function getRitualsUrl(thread_id?: string) {
  return router.resolve({
    name: thread_id ? ROUTE_RITUAL.URI_HOME : ROUTE_RITUAL.HOME,
    params: { thread_id },
  })
}

/**
 * Go to ritual(s) URL
 * @param thread_id Rituals URI, optional, if given, it will resolve the route to this ritual
 */
export const goToRituals = (thread_id?: string) => router.push(getRitualsUrl(thread_id))
